import React, { useState } from 'react';
import apiService from '../services/api';

const LandingPage = ({ onNavigateToClient, onNavigateToTrainer, onNavigateToAdmin }) => {
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [disclosureType, setDisclosureType] = useState('');
  const [contactForm, setContactForm] = useState({
    username: '',
    subject: '',
    summary: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactMessage, setContactMessage] = useState({ type: '', text: '' });

  const handlePortalAccess = (type) => {
    setDisclosureType(type);
    setShowDisclosure(true);
  };

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const trimmedUsername = contactForm.username.trim();
    const trimmedSubject = contactForm.subject.trim();
    const trimmedSummary = contactForm.summary.trim();

    if (!trimmedUsername || !trimmedSubject || !trimmedSummary) {
      setContactMessage({ type: 'error', text: 'Please complete all fields before submitting.' });
      return;
    }

    if (trimmedSubject.length > 200) {
      setContactMessage({ type: 'error', text: 'Subject must be 200 characters or fewer.' });
      return;
    }

    setContactSubmitting(true);
    setContactMessage({ type: '', text: '' });

    try {
      await apiService.createSupportTicket({
        username: trimmedUsername,
        subject: trimmedSubject,
        summary: trimmedSummary
      });

      setContactMessage({ type: 'success', text: 'Thanks for reaching out! Your ticket has been submitted.' });
      setContactForm({ username: '', subject: '', summary: '' });
    } catch (error) {
      setContactMessage({
        type: 'error',
        text: error.message || 'We were unable to submit your ticket. Please try again.'
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleAcceptDisclosure = () => {
    setShowDisclosure(false);
    switch (disclosureType) {
      case 'client':
        onNavigateToClient();
        break;
      case 'trainer':
        onNavigateToTrainer();
        break;
      case 'admin':
        onNavigateToAdmin();
        break;
      default:
        break;
    }
  };

  const styles = {
    container: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: '#0f172a',
      minHeight: '100vh',
      color: '#f1f5f9',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: '#1e293b',
      padding: '20px 0',
      boxShadow: '0 1px 0 rgba(0, 0, 0, 0.2)',
      borderBottom: '1px solid #334155'
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      cursor: 'pointer'
    },
    logoIcon: {
      width: '50px',
      height: '50px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      objectFit: 'contain'
    },
    logoText: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#f1f5f9'
    },
    nav: {
      display: 'flex',
      gap: '30px',
      alignItems: 'center'
    },
    navLink: {
      color: '#cbd5e1',
      textDecoration: 'none',
      fontSize: '1.1rem',
      fontWeight: '500',
      padding: '10px 20px',
      borderRadius: '25px',
      transition: 'all 0.3s ease',
      background: '#334155',
      border: '1px solid #475569'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      textAlign: 'center'
    },
    heroTitle: {
      fontSize: '4rem',
      fontWeight: '800',
      marginBottom: '20px',
      color: '#f1f5f9',
      lineHeight: '1.2'
    },
    heroSubtitle: {
      fontSize: '1.5rem',
      marginBottom: '40px',
      color: '#cbd5e1',
      maxWidth: '600px',
      lineHeight: '1.6'
    },
    portalGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '30px',
      maxWidth: '900px',
      width: '100%',
      marginTop: '40px'
    },
    portalCard: {
      background: '#1e293b',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      padding: '40px 30px',
      textAlign: 'center',
      border: '1px solid #334155',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden'
    },
    portalCardHover: {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
      border: '1px solid #475569'
    },
    portalIcon: {
      fontSize: '4rem',
      marginBottom: '20px',
      display: 'block'
    },
    portalTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      marginBottom: '15px',
      color: '#f1f5f9'
    },
    portalDescription: {
      fontSize: '1rem',
      color: '#cbd5e1',
      lineHeight: '1.6',
      marginBottom: '25px'
    },
    portalButton: {
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
      width: '100%'
    },
    features: {
      background: '#1e293b',
      padding: '80px 20px',
      marginTop: '60px'
    },
    featuresContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    featuresTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: '50px',
      color: '#f39c12'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '30px'
    },
    about: {
      background: '#0f172a',
      padding: '80px 20px',
      borderTop: '1px solid #1e293b',
      borderBottom: '1px solid #1e293b'
    },
    aboutContent: {
      maxWidth: '1100px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '40px',
      alignItems: 'center'
    },
    aboutText: {
      textAlign: 'left'
    },
    aboutTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#f39c12'
    },
    aboutParagraph: {
      fontSize: '1.05rem',
      color: '#cbd5e1',
      lineHeight: '1.7',
      marginBottom: '16px'
    },
    aboutHighlights: {
      display: 'grid',
      gap: '15px',
      marginTop: '20px'
    },
    aboutHighlightItem: {
      background: 'rgba(59, 130, 246, 0.12)',
      border: '1px solid rgba(59, 130, 246, 0.25)',
      borderRadius: '12px',
      padding: '15px 20px',
      color: '#93c5fd',
      fontSize: '0.95rem',
      lineHeight: '1.5'
    },
    aboutStatsCard: {
      background: '#1e293b',
      borderRadius: '18px',
      padding: '30px',
      border: '1px solid #334155',
      boxShadow: '0 15px 35px rgba(15, 23, 42, 0.45)',
      display: 'grid',
      gap: '20px'
    },
    aboutStat: {
      textAlign: 'left'
    },
    aboutStatValue: {
      fontSize: '2.2rem',
      fontWeight: '700',
      color: '#38bdf8'
    },
    aboutStatLabel: {
      fontSize: '1rem',
      color: '#cbd5e1',
      marginTop: '6px'
    },
    featureCard: {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '15px',
      padding: '30px',
      textAlign: 'center',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    featureIcon: {
      fontSize: '3rem',
      marginBottom: '20px',
      color: '#f39c12'
    },
    featureTitle: {
      fontSize: '1.3rem',
      fontWeight: '600',
      marginBottom: '15px',
      color: 'white'
    },
    featureDescription: {
      fontSize: '1rem',
      color: 'rgba(255, 255, 255, 0.8)',
      lineHeight: '1.6'
    },
    contact: {
      background: '#0b1120',
      padding: '80px 20px'
    },
    contactContent: {
      maxWidth: '900px',
      margin: '0 auto',
      textAlign: 'center'
    },
    contactTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '15px',
      color: '#38bdf8'
    },
    contactSubtitle: {
      fontSize: '1.1rem',
      color: '#cbd5e1',
      marginBottom: '35px',
      lineHeight: '1.6'
    },
    contactForm: {
      background: '#1e293b',
      borderRadius: '16px',
      padding: '35px',
      border: '1px solid #334155',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.45)',
      textAlign: 'left'
    },
    contactInput: {
      width: '100%',
      padding: '12px 14px',
      borderRadius: '10px',
      border: '1px solid #334155',
      background: '#0f172a',
      color: '#f8fafc',
      fontSize: '1rem',
      marginBottom: '20px'
    },
    contactTextarea: {
      width: '100%',
      minHeight: '160px',
      padding: '14px',
      borderRadius: '12px',
      border: '1px solid #334155',
      background: '#0f172a',
      color: '#f8fafc',
      fontSize: '1rem',
      resize: 'vertical',
      marginBottom: '25px',
      lineHeight: '1.6'
    },
    contactButton: {
      width: '100%',
      padding: '15px',
      borderRadius: '12px',
      border: 'none',
      background: 'linear-gradient(135deg, #38bdf8 0%, #2563eb 100%)',
      color: '#f8fafc',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 12px 25px rgba(37, 99, 235, 0.35)'
    },
    contactButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    contactMessage: {
      marginTop: '15px',
      fontSize: '0.95rem',
      fontWeight: '500'
    },
    contactMessageSuccess: {
      color: '#34d399'
    },
    contactMessageError: {
      color: '#f87171'
    },
    footer: {
      background: 'rgba(0, 0, 0, 0.2)',
      padding: '40px 20px',
      textAlign: 'center',
      color: 'rgba(255, 255, 255, 0.8)'
    },
    disclosureModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    disclosureContent: {
      background: 'white',
      borderRadius: '15px',
      padding: '40px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '80vh',
      overflowY: 'auto',
      color: '#2c3e50'
    },
    disclosureTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#2c3e50',
      textAlign: 'center'
    },
    disclosureText: {
      fontSize: '1rem',
      lineHeight: '1.6',
      marginBottom: '30px',
      color: '#555'
    },
    disclosureButtons: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center'
    },
    acceptButton: {
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    declineButton: {
      background: 'transparent',
      color: '#e74c3c',
      border: '2px solid #e74c3c',
      padding: '15px 30px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    }
  };

  const features = [
    {
      icon: '',
      title: 'Expert Trainers',
      description: 'Connect with experienced dog trainers who specialize in your specific needs.'
    },
    {
      icon: '',
      title: 'Virtual Sessions',
      description: 'Get professional training from the comfort of your home through secure video calls.'
    },
    {
      icon: '',
      title: 'Easy Access',
      description: 'Simple booking, session management, and progress tracking all in one platform.'
    },
    {
      icon: '',
      title: 'Progress Tracking',
      description: 'Monitor your dog\'s training progress with detailed summaries and actionable checklists.'
    },
    {
      icon: '',
      title: 'Secure & Safe',
      description: 'Your data and sessions are protected with enterprise-grade security measures.'
    },
    {
      icon: '',
      title: 'Transparent Pricing',
      description: 'Flexible pricing with transparent rates and no hidden fees.'
    }
  ];

  const disclosureText = {
    client: `By accessing the Client Portal, you agree to the following terms and conditions:

1. **Service Agreement**: You understand that Pheefee provides virtual dog training services through trainers.

2. **Payment Terms**: Sessions are billed at the trainer's published rates. The first 10 minutes are charged at a fixed rate, with additional time charged per minute.

3. **Session Recording**: Sessions may be recorded for quality assurance and training purposes. You will be notified before recording begins.

4. **Liability**: While our trainers are professionals, you understand that dog training involves inherent risks and agree to hold Pheefee harmless for any incidents.

5. **Data Privacy**: Your personal information and session data will be protected according to our privacy policy.

6. **Cancellation Policy**: Cancellations must be made at least 2 hours before your scheduled session to avoid charges.

Do you agree to these terms and wish to proceed to the Client Portal?`,

    trainer: `By accessing the Trainer Portal, you agree to the following terms and conditions:

1. **Independent Contractor Status**: You understand that you are an independent contractor, not an employee of Pheefee.

2. **Professional Standards**: You agree to maintain the highest professional standards and follow all applicable laws and regulations.

3. **Payment Terms**: You will receive payment on the first Monday of each month for completed sessions, minus platform fees.

4. **Background Check**: You consent to background checks and agree to provide all required documentation.

5. **Insurance Requirements**: You must maintain appropriate liability insurance and provide proof of coverage.

6. **Platform Rules**: You agree to follow all platform guidelines and maintain professional conduct during sessions.

7. **Data Security**: You will protect client information and maintain confidentiality at all times.

Do you agree to these terms and wish to proceed to the Trainer Portal?`,

    admin: `By accessing the Admin Portal, you agree to the following terms and conditions:

1. **Administrative Access**: You are authorized to access administrative functions and sensitive data.

2. **Data Security**: You will maintain the highest level of security and confidentiality for all platform data.

3. **Compliance**: You will ensure all operations comply with applicable laws and regulations.

4. **Audit Trail**: All administrative actions will be logged and monitored.

5. **Access Control**: You will not share administrative credentials or access with unauthorized personnel.

Do you agree to these terms and wish to proceed to the Admin Portal?`
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo}>
            <img 
              src="/images/logo.png" 
              alt="Pheefee Logo" 
              style={styles.logoIcon}
            />
            <span style={styles.logoText}>Pheefee</span>
          </div>
          <nav style={styles.nav}>
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#about" style={styles.navLink}>About</a>
            <a href="#contact" style={styles.navLink}>Contact</a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        <h1 style={styles.heroTitle}>
          Professional Dog Training<br />
          <span style={{ fontSize: '3rem' }}>On Demand</span>
        </h1>
        <p style={styles.heroSubtitle}>
          Connect with dog trainers for personalized virtual training sessions. 
          Get expert guidance, track progress, and achieve your training goals from anywhere.
        </p>

        <div style={styles.portalGrid}>
          <div 
            style={styles.portalCard}
            onClick={() => handlePortalAccess('client')}
          >
            <span style={styles.portalIcon}>Client</span>
            <h2 style={styles.portalTitle}>Client Portal</h2>
            <p style={styles.portalDescription}>
              Book training sessions, track your dog's progress, access session summaries, 
              and manage your training journey with professional guidance.
            </p>
            <button style={styles.portalButton}>
              Access Client Portal
            </button>
          </div>

          <div 
            style={styles.portalCard}
            onClick={() => handlePortalAccess('trainer')}
          >
            <span style={styles.portalIcon}>Trainer</span>
            <h2 style={styles.portalTitle}>Trainer Portal</h2>
            <p style={styles.portalDescription}>
              Manage your training business, connect with clients, conduct virtual sessions, 
              and grow your professional practice with our platform.
            </p>
            <button style={styles.portalButton}>
              Access Trainer Portal
            </button>
          </div>
        </div>
      </main>

      {/* About Section */}
      <section id="about" style={styles.about}>
        <div style={styles.aboutContent}>
          <div style={styles.aboutText}>
            <h2 style={styles.aboutTitle}>About Pheefee</h2>
            <p style={styles.aboutParagraph}>
              Pheefee pairs pet guardians with trainers who are passionate about helping
              dogs thrive. We blend proven training methodologies with real-time virtual coaching so you
              can build consistent habits that fit your lifestyle.
            </p>
            <p style={styles.aboutParagraph}>
              From puppy socialization to advanced behavior modification, our trainers collaborate with you
              to create actionable plans, track progress, and celebrate every milestone along the journey.
              Training should feel empowering, and we make sure you have the tools to succeed long after
              each session ends.
            </p>
            <div style={styles.aboutHighlights}>
              <div style={styles.aboutHighlightItem}>Trainers are screened for specialty experience</div>
              <div style={styles.aboutHighlightItem}>Structured session recaps and personalized task lists</div>
              <div style={styles.aboutHighlightItem}>Secure video platform with integrated payments and scheduling</div>
            </div>
          </div>
          <div style={styles.aboutStatsCard}>
            <div style={styles.aboutStat}>
              <div style={styles.aboutStatValue}>15+</div>
              <div style={styles.aboutStatLabel}>Specialty training areas covered</div>
            </div>
            <div style={styles.aboutStat}>
              <div style={styles.aboutStatValue}>98%</div>
              <div style={styles.aboutStatLabel}>Client satisfaction score across recent sessions</div>
            </div>
            <div style={styles.aboutStat}>
              <div style={styles.aboutStatValue}>24/7</div>
              <div style={styles.aboutStatLabel}>Flexible scheduling to match your routine</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={styles.features}>
        <div style={styles.featuresContent}>
          <h2 style={styles.featuresTitle}>Why Choose Pheefee?</h2>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div key={index} style={styles.featureCard}>
                <span style={styles.featureIcon}>{feature.icon}</span>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={styles.contact}>
        <div style={styles.contactContent}>
          <h2 style={styles.contactTitle}>Contact Us</h2>
          <p style={styles.contactSubtitle}>
            Have a question, feature request, or need help with your account? Send us a ticket and our team
            will get back to you within one business day.
          </p>
          <form style={styles.contactForm} onSubmit={handleContactSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '8px', fontWeight: 600 }}>
                Username
              </label>
              <input
                style={styles.contactInput}
                type="text"
                value={contactForm.username}
                onChange={(e) => handleContactChange('username', e.target.value)}
                placeholder="Enter the username tied to your account"
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '8px', fontWeight: 600 }}>
                Subject
              </label>
              <input
                style={styles.contactInput}
                type="text"
                value={contactForm.subject}
                onChange={(e) => handleContactChange('subject', e.target.value)}
                placeholder="How can we help?"
                maxLength={200}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '8px', fontWeight: 600 }}>
                Issue Summary
              </label>
              <textarea
                style={styles.contactTextarea}
                value={contactForm.summary}
                onChange={(e) => handleContactChange('summary', e.target.value)}
                placeholder="Share details about the issue or question you're experiencing..."
              />
            </div>
            <button
              type="submit"
              style={{
                ...styles.contactButton,
                ...(contactSubmitting ? styles.contactButtonDisabled : {})
              }}
              disabled={contactSubmitting}
            >
              {contactSubmitting ? 'Submitting Ticket...' : 'Submit Support Ticket'}
            </button>
            {contactMessage.text && (
              <div
                style={{
                  ...styles.contactMessage,
                  ...(contactMessage.type === 'success'
                    ? styles.contactMessageSuccess
                    : styles.contactMessageError)
                }}
              >
                {contactMessage.text}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>&copy; 2024 Pheefee. All rights reserved. | Privacy Policy | Terms of Service</p>
      </footer>

      {/* Disclosure Modal */}
      {showDisclosure && (
        <div style={styles.disclosureModal}>
          <div style={styles.disclosureContent}>
            <h2 style={styles.disclosureTitle}>
              {disclosureType === 'client' && 'Client Portal Agreement'}
              {disclosureType === 'trainer' && 'Trainer Portal Agreement'}
              {disclosureType === 'admin' && 'Admin Portal Agreement'}
            </h2>
            <div style={styles.disclosureText}>
              {disclosureText[disclosureType]}
            </div>
            <div style={styles.disclosureButtons}>
              <button 
                style={styles.acceptButton}
                onClick={handleAcceptDisclosure}
              >
                I Agree - Continue
              </button>
              <button 
                style={styles.declineButton}
                onClick={() => setShowDisclosure(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;

